import React, { useEffect, useRef, useState } from 'react';
import { Html5QrcodeScanner, Html5QrcodeScanType, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Camera, X } from 'lucide-react';

const BarcodeScannerNew = ({ onDetected, onClose, isOpen }) => {
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [lastCode, setLastCode] = useState(null);
  
  useEffect(() => {
    if (!isOpen) return;

    let html5QrcodeScanner = null;
    
    const config = {
      fps: 10,
      qrbox: { width: 250, height: 150 },
      rememberLastUsedCamera: true,
      supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
      formatsToSupport: [
        Html5QrcodeSupportedFormats.EAN_13,
        Html5QrcodeSupportedFormats.EAN_8,
        Html5QrcodeSupportedFormats.CODE_128,
        Html5QrcodeSupportedFormats.CODE_39,
        Html5QrcodeSupportedFormats.UPC_A,
        Html5QrcodeSupportedFormats.UPC_E,
        Html5QrcodeSupportedFormats.ITF,
        Html5QrcodeSupportedFormats.CODE_93,
        Html5QrcodeSupportedFormats.CODABAR
      ]
    };

    const onScanSuccess = (decodedText, decodedResult) => {
      // Evitar múltiples lecturas del mismo código
      if (decodedText === lastCode) return;
      
      setLastCode(decodedText);
      console.log('Código detectado:', decodedText, decodedResult);
      
      // Vibrar si es posible
      if (navigator.vibrate) {
        navigator.vibrate(200);
      }
      
      // Limpiar el código sin espacios o caracteres extraños
      const cleanCode = decodedText.trim().replace(/\s/g, '');
      
      // Llamar inmediatamente a onDetected para agregar al carrito
      onDetected(cleanCode);
      
      // Detener el escáner después de notificar
      setTimeout(() => {
        if (html5QrcodeScanner) {
          html5QrcodeScanner.clear();
        }
      }, 100);
    };

    const onScanFailure = (error) => {
      // Ignorar errores de escaneo continuos (normal cuando no hay código)
      if (error.indexOf('No QR code found') === -1) {
        console.warn('Error de escaneo:', error);
      }
    };

    // Pequeño delay para asegurar que el DOM está listo
    const timeoutId = setTimeout(() => {
      try {
        setScanning(true);
        setError(null);
        
        html5QrcodeScanner = new Html5QrcodeScanner(
          "qr-reader",
          config,
          false
        );
        
        html5QrcodeScanner.render(onScanSuccess, onScanFailure);
      } catch (err) {
        console.error('Error iniciando escáner:', err);
        setError('No se pudo iniciar el escáner. Verifica los permisos de cámara.');
        setScanning(false);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (html5QrcodeScanner) {
        try {
          html5QrcodeScanner.clear();
        } catch (err) {
          console.warn('Error al limpiar escáner:', err);
        }
      }
    };
  }, [isOpen, onDetected, lastCode]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-4 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Camera className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-bold">Escanear Código de Barras</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error ? (
          <div className="text-red-600 text-center p-8 bg-red-50 rounded-lg">
            <p>{error}</p>
            <button onClick={onClose} className="mt-4 btn-secondary">
              Cerrar
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div id="qr-reader" className="w-full"></div>
            
            {!scanning && (
              <div className="text-center text-gray-500">
                <div className="animate-pulse">Iniciando cámara...</div>
              </div>
            )}
            
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Consejos:</strong>
              </p>
              <ul className="text-xs text-blue-700 mt-1 space-y-1">
                <li>• Mantén el código dentro del recuadro</li>
                <li>• Asegúrate de tener buena iluminación</li>
                <li>• Mantén el dispositivo estable</li>
                <li>• El código debe estar limpio y sin reflejos</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BarcodeScannerNew;
